import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";

const MyCarousel = ({ items, autoSlide = false, autoSlideInterval = 4000, onItemClick }) => {
  const [curr, setCurr] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoSlide);
  const [imageErrors, setImageErrors] = useState(new Set());

  const prev = () =>
    setCurr((curr) => (curr === 0 ? items.length - 1 : curr - 1));
  
  const next = () =>
    setCurr((curr) => (curr === items.length - 1 ? 0 : curr + 1));

  const goToSlide = (index) => {
    setCurr(index);
  };

  const handleImageError = (index) => {
    setImageErrors(prev => new Set([...prev, index]));
  };

  useEffect(() => {
    if (!isPlaying) return;
    const slideInterval = setInterval(next, autoSlideInterval);
    return () => clearInterval(slideInterval);
  }, [isPlaying, autoSlideInterval, curr]);

  const getImageUrl = (item) => {
    if (typeof item === "string") return item;
    if (typeof item === "object" && item && item.url) return item.url;
    return null;
  };

  const getItemName = (item, index) => {
    if (typeof item === "object" && item && item.name) return item.name;
    return `Vehículo ${index + 1}`;
  };

  const getItemYear = (item) => {
    if (typeof item === "object" && item && item.year) return item.year;
    return null;
  };

  const handleItemClick = (item) => {
    if (onItemClick && typeof item === "object" && item) {
      onItemClick(item);
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="w-full h-96 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center border border-gray-400">
        <div className="text-center text-gray-600">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-400 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-lg font-medium">No hay vehículos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      {/* Main Carousel Container */}
      <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-gray-100 border border-gray-300">
        <div className="relative w-full min-h-[300px] max-h-[500px] mb-8">
          {/* Slides */}
          <div 
            className="flex transition-transform ease-out duration-700"
            style={{ transform: `translateX(-${curr * 100}%)` }}
          >
            {items.map((item, i) => {
              const imageUrl = getImageUrl(item);
              const itemName = getItemName(item, i);
              const itemYear = getItemYear(item);
              const hasError = imageErrors.has(i);
              const isClickable = typeof item === "object" && item && onItemClick;

              return (
                <div className="w-full flex-shrink-0 p-1" key={i}>
                  <div 
                    className={`relative w-full group ${isClickable ? 'cursor-pointer' : ''}`}
                    onClick={() => handleItemClick(item)}
                  >
                    {hasError || !imageUrl ? (
                      <div className="w-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center rounded-2xl min-h-[300px] border border-gray-400">
                        <div className="text-center text-gray-600">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gray-500 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium">Imagen no disponible</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <img 
                          src={imageUrl} 
                          className="w-full max-h-[400px] object-contain rounded-2xl shadow-lg transition-transform duration-700 group-hover:scale-105 mx-auto block"
                          alt={itemName}
                          onError={() => handleImageError(i)}
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {/* <div className="bg-gray-900/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-gray-600">
                            <p className="text-sm font-bold">{itemName}</p>
                            {itemYear && <p className="text-xs opacity-80">Año: {itemYear}</p>}
                          </div> */}
                        </div>
                        {isClickable && (
                          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-gray-700 text-white px-3 py-1 rounded-full text-xs font-medium border border-gray-600">
                              Ver más →
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation Buttons */}
          <div className="absolute inset-y-0 left-0 flex items-center">
            <button
              onClick={prev}
              className="ml-4 p-3 rounded-full bg-gray-100/90 backdrop-blur-sm text-gray-800 hover:bg-gray-200 hover:scale-110 transform transition-all duration-200 shadow-lg hover:shadow-xl border border-gray-300"
              aria-label="Anterior"
            >
              <ChevronLeft size={24} />
            </button>
          </div>
          
          <div className="absolute inset-y-0 right-0 flex items-center">
            <button
              onClick={next}
              className="mr-4 p-3 rounded-full bg-gray-100/90 backdrop-blur-sm text-gray-800 hover:bg-gray-200 hover:scale-110 transform transition-all duration-200 shadow-lg hover:shadow-xl border border-gray-300"
              aria-label="Siguiente"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-6 space-x-2 relative z-20">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full border ${
              index === curr
                ? "w-8 h-3 bg-gradient-to-r from-gray-700 to-gray-900 border-gray-600"
                : "w-3 h-3 bg-gray-400 hover:bg-gray-500 border-gray-500"
            }`}
            aria-label={`Ir a la imagen ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      {isPlaying && (
        <div className="mt-4 w-full bg-gray-300 rounded-full h-1 overflow-hidden relative z-20 border border-gray-400">
          <div 
            className="h-full bg-gradient-to-r from-gray-700 to-gray-900 transition-all duration-100 ease-linear"
            style={{
              width: `${((Date.now() % autoSlideInterval) / autoSlideInterval) * 100}%`
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MyCarousel;